import { Fragment, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  CircleCheck,
  Download,
  Filter,
  ImageOff,
  Loader2,
  Package,
  Plus,
  Printer,
  QrCode,
  Pencil,
  PenTool,
  Search,
  Trash2,
  TriangleAlert,
  Upload,
  X,
} from 'lucide-react';
import { Page } from '../../components/layout/Page.js';
import { DataTable, type Column } from '../../components/data/DataTable.js';
import { Button } from '../../components/ui/button.js';
import { ConfirmDialog } from '../../components/ui/dialog.js';
import { deleteRow } from '../../db/mutations.js';
import { Input, Select } from '../../components/ui/field.js';
import { Badge, Code, EmptyState } from '../../components/ui/primitives.js';
import { hasOwnName, useMasterOptions, useMoulds, type MouldRow } from '../../db/queries.js';
import { useCan } from '../../auth/AuthProvider.js';
import { RULE_BY_ID } from '../quality/rules.js';
import { freshnessOf } from '../tracking/freshness.js';
import { Menu, MenuItem, MenuLabel, MenuSeparator } from '../../components/ui/menu.js';
import { MouldLabelDialog } from './MouldLabelDialog.js';
import { LabelSheetDialog } from './LabelSheetDialog.js';
import { ColumnChooser, ViewMenu } from './CatalogToolbar.js';
import { MouldImportDialog } from './MouldImportDialog.js';
import { MouldBulkEditDialog } from './MouldBulkEditDialog.js';
import { EngraveBatchDialog } from './EngraveBatchDialog.js';
import {
  DEFAULT_COLUMNS,
  readColumns,
  readViews,
  writeColumns,
  writeViews,
  type SavedView,
} from './catalogViews.js';
import {
  DEFAULT_EXPORT_FORMAT,
  EXPORT_FORMATS,
  exportMouldsToExcel,
  type ExportFormat,
  type ExportResult,
} from './exportMoulds.js';
import { MASTER_SHEET } from './masterSheet.js';
import { projectLabel } from './projectLabel.js';
import { placeOf, useMouldLocations, whereabouts } from './useMouldLocations.js';
import { describeDownload, downloadDate, useEngraveDownloads } from './useEngrave.js';
import { cn } from '../../lib/cn.js';


/**
 * The catalog list - the screen the Inventory Manager lives in.
 *
 * Search is instant because it runs against IndexedDB, not the network: no
 * debounce, no spinner, no empty flash between keystrokes. That responsiveness
 * is the most visible dividend of the local-first architecture.
 *
 * The full view mirrors "MOLD LIST FOR MIRA ROAD" so the people migrating off
 * that spreadsheet can check a row against the one they already know.
 */
export function MouldListPage() {
  const navigate = useNavigate();
  const canCreate = useCan('create', 'moulds');
  // Taking the catalog off the system is its own permission in the matrix, held
  // separately from being able to read it on screen.
  const canExport = useCan('export', 'moulds');
  // Deleting is admin-only in the matrix; bulk deleting should be no easier.
  const canDelete = useCan('delete', 'moulds');

  /*
   * Filters come from the address bar.
   *
   * So that a number on the dashboard can be a link. "20 unassigned" is only
   * useful if clicking it lands on exactly those twenty, and a filter that
   * lives in component state cannot be linked to, shared, or reopened after a
   * refresh. Held in the URL, all three come free.
   */
  const [params, setParams] = useSearchParams();
  const search = params.get('q') ?? '';
  const projectId = params.get('project') ?? '';
  const ownerCompanyId = params.get('owner') ?? '';
  const toolTypeId = params.get('toolType') ?? '';
  const gap = params.get('gap') ?? '';

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const setSearch = (v: string) => setParam('q', v);
  const setProjectId = (v: string) => setParam('project', v);
  const setOwnerCompanyId = (v: string) => setParam('owner', v);
  const setToolTypeId = (v: string) => setParam('toolType', v);

  // Open the filter drawer when arriving with filters already applied, so it is
  // obvious why the list is short.
  const [showFilters, setShowFilters] = useState(
    () => Boolean(params.get('project') || params.get('owner') || params.get('toolType')),
  );

  const projects = useMasterOptions('projects');
  const companies = useMasterOptions('companies');
  const toolTypes = useMasterOptions('toolTypes');

  // Which columns this person wants, and the filter sets they have named. Both
  // per-browser - see catalogViews for why they are not synced.
  const [columnKeys, setColumnKeys] = useState<string[]>(() => readColumns() ?? DEFAULT_COLUMNS);
  const [views, setViews] = useState<SavedView[]>(() => readViews());
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [engraveOpen, setEngraveOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [labelSheet, setLabelSheet] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [labelFor, setLabelFor] = useState<MouldRow | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportProgress, setExportProgress] = useState<{ done: number; total: number } | null>(null);
  const [exportResult, setExportResult] = useState<ExportResult | null>(null);

  const gapRule = gap ? RULE_BY_ID[gap] : undefined;
  const rows = useMoulds({
    search, projectId, ownerCompanyId, toolTypeId,
    gapTest: gapRule?.test,
    gapKey: gapRule?.id,
  });
  // The whole catalog, so "export everything" does not depend on first clearing
  // the filters. Same Dexie query, so it costs one extra live subscription.
  const allRows = useMoulds({});
  const places = useMouldLocations();
  // Which moulds' engrave files were downloaded already: the tick column.
  const engraved = useEngraveDownloads();
  const activeFilters = [projectId, ownerCompanyId, toolTypeId, gap].filter(Boolean).length;
  /** True when the list on screen is a subset of the catalog. */
  const isNarrowed = Boolean(search.trim()) || activeFilters > 0;

  async function deleteSelected() {
    setDeleting(true);
    try {
      // One mutation per row, like the bulk edit: each becomes its own outbox
      // entry, so a rejection on one mould does not strand the others.
      for (const row of selectedRows) await deleteRow('moulds', row.id);
      setSelected(new Set());
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  }

  function chooseColumns(next: string[]) {
    setColumnKeys(next);
    writeColumns(next);
  }

  function saveCurrentView(name: string) {
    const view: SavedView = {
      id: crypto.randomUUID(),
      name: name.trim(),
      search,
      projectId,
      ownerCompanyId,
      toolTypeId,
      columns: columnKeys,
    };
    const next = [...views.filter((v) => v.name !== view.name), view];
    setViews(next);
    writeViews(next);
  }

  function applyView(view: SavedView) {
    setSearch(view.search ?? '');
    setProjectId(view.projectId ?? '');
    setOwnerCompanyId(view.ownerCompanyId ?? '');
    setToolTypeId(view.toolTypeId ?? '');
    if (view.columns?.length) chooseColumns(view.columns);
    setShowFilters(false);
  }

  function deleteView(id: string) {
    const next = views.filter((v) => v.id !== id);
    setViews(next);
    writeViews(next);
  }

  async function exportRows(which: 'visible' | 'all', format: ExportFormat = DEFAULT_EXPORT_FORMAT) {
    const data = which === 'all' ? allRows : rows;
    if (!data?.length || exporting) return;
    setExporting(true);
    setExportError(null);
    setExportResult(null);
    try {
      setExportResult(
        await exportMouldsToExcel({
          rows: data,
          places,
          label: which === 'visible' && isNarrowed ? 'filtered' : undefined,
          format,
          onProgress: (done, total) => setExportProgress({ done, total }),
        }),
      );
    } catch (e) {
      setExportError(e instanceof Error ? e.message : 'The export could not be created.');
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  }

  /**
   * The client's Master Sheet (MOLD LIST_63), column for column: its headings,
   * word for word, in its order - the same as the Excel export, so the screen
   * and the file read alike (masterSheet.ts holds the headings for both). After
   * them, two columns only the website has: the QR label and the engrave tick.
   *
   * "Location" is the one that does not come off the mould record: it belongs to
   * the scan ledger rather than the form - see useMouldLocations for why it is
   * fetched instead of stored.
   */
  const columns = useMemo<Array<Column<MouldRow>>>(
    () => [
      {
        key: 'catalogNo',
        header: MASTER_SHEET.sno,
        // Pinned so the row you are reading stays identified once the table is
        // scrolled sideways. Only the first column can do this reliably: any
        // later one needs a hard-coded left offset that has to match a rendered
        // width the browser is free to change.
        headerClassName: 'sticky left-0 z-20 w-14 bg-surface-sunken',
        className:
          'sticky left-0 z-10 w-14 bg-surface tabular text-subtle-foreground group-hover:bg-surface-sunken',
        // A display sequence, 1 to N in the order shown - after search,
        // filters and sorting. Not the stored catalog number, not the sheet's
        // row and not an id: those stay on the record, untouched.
        render: (_m, index) => index + 1,
      },
      {
        key: 'owner',
        header: MASTER_SHEET.owner,
        sortValue: (m) => m.ownerCompanyName,
        render: (m) => m.ownerCompanyName ?? <Missing />,
      },
      {
        key: 'customer',
        header: MASTER_SHEET.customer,
        hideOnMobile: true,
        sortValue: (m) => m.customerCompanyName,
        render: (m) => m.customerCompanyName ?? <Missing />,
      },
      {
        key: 'photo',
        header: MASTER_SHEET.image,
        headerClassName: 'w-14',
        className: 'w-14',
        hideOnMobile: true,
        render: (m) => <Thumb src={m.photoUrl} alt={m.name} count={m.photoCount} />,
      },
      {
        // The project, as the sheet's CODE AND NAME column holds it.
        key: 'project',
        header: MASTER_SHEET.codeAndName,
        // Without a floor these wrap to three lines and set the height of every
        // row in the table.
        headerClassName: 'min-w-40',
        className: 'min-w-40',
        // Code and name, as the Excel export writes it ("E9707-HUNGRY HUNGRY HIPPO LAUNCHER").
        sortValue: (m) => projectLabel(m),
        render: (m) => projectLabel(m) || <Missing />,
      },
      {
        key: 'assetCode',
        header: MASTER_SHEET.moldNumber,
        headerClassName: 'min-w-32',
        className: 'min-w-32',
        primary: true,
        sortValue: (m) => m.assetCode,
        render: (m) => (
          <div className="flex items-center gap-2">
            <Code className="font-semibold">{m.assetCode}</Code>
            {m._pending === 1 ? (
              <span
                title="Not yet synced"
                className="size-1.5 shrink-0 rounded-full bg-warning"
                aria-label="Not yet synced"
              />
            ) : null}
          </div>
        ),
      },
      {
        // Its own column, as on the sheet. On a phone it joins the code on the
        // card's title line.
        key: 'mouldName',
        header: MASTER_SHEET.moldName,
        headerClassName: 'min-w-56',
        className: 'min-w-56',
        primary: true,
        sortValue: (m) => (hasOwnName(m) ? m.name : ''),
        render: (m) => (hasOwnName(m) ? <span className="font-medium">{m.name}</span> : <Missing />),
      },
      {
        key: 'toolType',
        header: MASTER_SHEET.toolType,
        sortValue: (m) => m.toolTypeName,
        render: (m) => m.toolTypeName ?? <Missing />,
      },
      {
        key: 'cavities',
        header: MASTER_SHEET.cavities,
        hideOnMobile: true,
        sortValue: (m) => m.cavities,
        render: (m) => <span className="tabular">{m.cavities ?? <Missing />}</span>,
      },
      {
        key: 'runnerType',
        header: MASTER_SHEET.runnerType,
        hideOnMobile: true,
        sortValue: (m) => m.runnerTypeName,
        render: (m) => m.runnerTypeName ?? <Missing />,
      },
      {
        key: 'materialGrade',
        header: MASTER_SHEET.material,
        headerClassName: 'min-w-36',
        className: 'min-w-36',
        hideOnMobile: true,
        sortValue: (m) => m.materialGradeName,
        render: (m) => m.materialGradeName ?? <Missing />,
      },
      {
        key: 'gateType',
        header: MASTER_SHEET.gateType,
        hideOnMobile: true,
        sortValue: (m) => m.gateTypeName,
        render: (m) => m.gateTypeName ?? <Missing />,
      },
      {
        key: 'ejectionType',
        header: MASTER_SHEET.ejectionType,
        hideOnMobile: true,
        sortValue: (m) => m.ejectionTypeName,
        render: (m) => m.ejectionTypeName ?? <Missing />,
      },
      {
        key: 'mechanismType',
        header: MASTER_SHEET.mechanism,
        headerClassName: 'min-w-44',
        className: 'min-w-44',
        hideOnMobile: true,
        sortValue: (m) => m.mechanismTypeName,
        render: (m) => m.mechanismTypeName ?? <Missing />,
      },
      {
        key: 'coreCavityMaterial',
        header: MASTER_SHEET.coreCavityMaterial,
        hideOnMobile: true,
        sortValue: (m) => m.coreCavityMaterialName,
        render: (m) => m.coreCavityMaterialName ?? <Missing />,
      },
      {
        key: 'moldBaseMaterial',
        header: MASTER_SHEET.moldBaseMaterial,
        hideOnMobile: true,
        sortValue: (m) => m.moldBaseMaterialName,
        render: (m) => m.moldBaseMaterialName ?? <Missing />,
      },
      {
        key: 'moldSize',
        header: MASTER_SHEET.moldSize,
        hideOnMobile: true,
        sortValue: (m) => m.moldSize,
        render: (m) => <span className="tabular">{m.moldSize ?? <Missing />}</span>,
      },
      {
        key: 'designer',
        header: MASTER_SHEET.designer,
        hideOnMobile: true,
        sortValue: (m) => m.designerName,
        render: (m) => m.designerName ?? <Missing />,
      },
      {
        key: 'manufactureYear',
        header: MASTER_SHEET.manufactureYear,
        headerClassName: 'min-w-36',
        className: 'min-w-36',
        hideOnMobile: true,
        sortValue: (m) => m.manufactureYear,
        render: (m) => <span className="tabular">{m.manufactureYear ?? <Missing />}</span>,
      },
      {
        key: 'manufactureCompany',
        header: MASTER_SHEET.manufacturer,
        hideOnMobile: true,
        sortValue: (m) => m.manufactureCompanyName,
        render: (m) => m.manufactureCompanyName ?? <Missing />,
      },
      {
        key: 'toolTransfer',
        header: MASTER_SHEET.toolTransfer,
        hideOnMobile: true,
        sortValue: (m) => (m.toolTransfer == null ? '' : m.toolTransfer ? 'Yes' : 'No'),
        render: (m) => (m.toolTransfer == null ? <Missing /> : m.toolTransfer ? 'Yes' : 'No'),
      },
      {
        // The record's notes, which the sheet calls REMARKS (and imports as such).
        key: 'remarks',
        header: MASTER_SHEET.remarks,
        headerClassName: 'min-w-40',
        className: 'min-w-40 max-w-72',
        hideOnMobile: true,
        sortValue: (m) => m.description ?? '',
        render: (m) =>
          m.description ? (
            <span className="line-clamp-2" title={m.description}>
              {m.description}
            </span>
          ) : (
            <Missing />
          ),
      },
      {
        key: 'qrNumber',
        header: MASTER_SHEET.qrNumber,
        hideOnMobile: true,
        sortValue: (m) => m.qrNumber ?? '',
        render: (m) => (m.qrNumber ? <Code className="text-xs">{m.qrNumber}</Code> : <Missing />),
      },
      {
        key: 'location',
        header: MASTER_SHEET.location,
        sortValue: (m) => placeOf(places, m)?.location,
        render: (m) => {
          const place = placeOf(places, m);
          // Between a Mold OUT and the Mold IN at its destination, a mould is
          // on the road: say so, and where it came from.
          const where = whereabouts(m, place);
          if (where.inTransit) {
            return (
              <span className="whitespace-nowrap" title={where.label ?? undefined}>
                <span className="font-medium text-warning">In transit</span>
                {where.origin ? (
                  <span className="block text-xs text-subtle-foreground">{where.origin}</span>
                ) : null}
              </span>
            );
          }
          if (!place?.location) return <Missing />;
          /* The age of the answer, under the answer.
             A place on its own is an assertion with no date on it, and most of
             the ones on this catalog are months old. Printing the two together
             is the difference between "it is at Mira Road" and "it was at Mira
             Road in June", which are not the same claim. */
          const age = freshnessOf(place.lastScannedAt);
          return (
            <span className="whitespace-nowrap">
              {place.location}
              {place.store ? (
                <span className="text-subtle-foreground"> · {place.store}</span>
              ) : null}
              <span className={cn('block text-xs', age.tone)} title={age.meaning}>
                {age.label}
              </span>
            </span>
          );
        },
      },
      {
        key: 'qr',
        header: 'QR code',
        headerClassName: 'w-16',
        className: 'w-16',
        hideOnMobile: true,
        // Outside the row's click target: this opens a label, it does not
        // navigate, and a mis-click that prints is more annoying than one that
        // opens the record.
        render: (m) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setLabelFor(m);
            }}
            aria-label={`Show the QR label for ${m.assetCode}`}
            title={`Show the QR label for ${m.assetCode}`}
          >
            <QrCode className="size-4" />
          </Button>
        ),
      },
      {
        // Whether this mould's engrave file (the DXF for its plate) has been
        // downloaded, so a plate is not cut twice without meaning to. It can
        // always be downloaded again - from the mould page or Engrave files.
        key: 'engraved',
        header: 'Engrave file',
        hideOnMobile: true,
        sortValue: (m) => engraved[m.id]?.lastAt ?? '',
        render: (m) => {
          const d = engraved[m.id];
          if (!d) return <span className="text-xs text-subtle-foreground">Not yet</span>;
          return (
            <span className="inline-flex items-start gap-1.5 whitespace-nowrap" title={describeDownload(d)} data-testid="engrave-tick">
              <CircleCheck className="mt-0.5 size-4 shrink-0 text-success" aria-label="Engrave file downloaded" />
              <span className="leading-tight">
                <span className="block text-sm">Downloaded</span>
                <span className="block text-xs text-subtle-foreground">
                  {downloadDate(d.lastAt)}
                  {d.count > 1 ? ` · ${d.count}×` : ''}
                </span>
              </span>
            </span>
          );
        },
      },
    ],
    [places, engraved],
  );

  /**
   * Columns in the spreadsheet's order, filtered to the chosen ones.
   *
   * Filtered rather than reordered: the point of matching the client's sheet is
   * that a row reads the same way, and letting the order drift per person would
   * lose that for the sake of a preference nobody asked for. The mould code is
   * forced back in if somebody unticks everything, because a table of specs with
   * no identity column is not a table of anything.
   */
  const visibleColumns = useMemo(() => {
    const wanted = new Set(columnKeys.length ? columnKeys : DEFAULT_COLUMNS);
    wanted.add('assetCode');
    return columns.filter((c) => wanted.has(c.key));
  }, [columns, columnKeys]);

  const selectedRows = useMemo(
    () => (rows ?? []).filter((m) => selected.has(m.id)),
    [rows, selected],
  );

  return (
    <Page
      title="Mould Catalog"
      description={
        rows ? `${rows.length} mould${rows.length === 1 ? '' : 's'}` : 'Loading the catalog…'
      }
      actions={
        <>
          {/* One button when the list is the whole catalog, a choice when it is
              not - nobody should have to clear their filters to get a full
              export, or wonder which of the two they just downloaded. */}
          {/* The client's Master Sheet layout first - the default format, agreed
              on 24 Oct 2026 - and the two earlier layouts underneath. When the
              list is narrowed, both the narrowed list and the whole catalog are
              offered, so nobody has to clear their filters to get a full export. */}
          {!canExport ? null : (
            <Menu
              trigger={
                <Button
                  variant="secondary"
                  disabled={exporting || !allRows?.length}
                  title="Download the catalog as an Excel file"
                >
                  {exporting ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
                  Export
                </Button>
              }
            >
              {EXPORT_FORMATS.map(({ id, label }, i) => (
                <Fragment key={id}>
                  {i > 0 ? <MenuSeparator /> : null}
                  <MenuLabel>Excel - {label}</MenuLabel>
                  {isNarrowed ? (
                    <MenuItem onSelect={() => void exportRows('visible', id)}>These {rows?.length ?? 0} moulds</MenuItem>
                  ) : null}
                  <MenuItem onSelect={() => void exportRows('all', id)}>All {allRows?.length ?? 0} moulds</MenuItem>
                </Fragment>
              ))}
            </Menu>
          )}
          {canCreate ? (
            <>
              <Button
                variant="secondary"
                onClick={() => setEngraveOpen(true)}
                disabled={!allRows?.length}
                title="Generate the engrave (DXF) file of every mould, as one ZIP"
              >
                <PenTool className="size-4" /> Engrave files
              </Button>
              <Button variant="secondary" onClick={() => setImportOpen(true)}>
                <Upload className="size-4" /> Import
              </Button>
              <Button variant="primary" asChild>
                <Link to="/moulds/new">
                  <Plus className="size-4" /> Add mould
                </Link>
              </Button>
            </>
          ) : null}
        </>
      }
      toolbar={
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by ID, name or size…"
                className="pl-9"
                type="search"
                aria-label="Search moulds"
              />
            </div>
            <Button
              variant={showFilters || activeFilters ? 'outline' : 'secondary'}
              onClick={() => setShowFilters((v) => !v)}
              aria-expanded={showFilters}
            >
              <Filter className="size-4" />
              <span className="hidden sm:inline">Filters</span>
              {activeFilters ? (
                <Badge tone="primary" className="ml-0.5">
                  {activeFilters}
                </Badge>
              ) : null}
            </Button>
            {/* Both desktop only: below `lg` the table is already a card list,
                so there are no columns to choose between. */}
            <ColumnChooser
              className="hidden lg:inline-flex"
              columns={columns}
              selected={columnKeys}
              onChange={chooseColumns}
            />
            <ViewMenu
              className="hidden lg:inline-flex"
              views={views}
              dirty={isNarrowed}
              onApply={applyView}
              onSave={saveCurrentView}
              onDelete={deleteView}
            />
          </div>

          {/* Arriving from a dashboard number, the list is already narrowed to
              the records behind it. Say which, and offer the way out - a short
              list with no visible reason reads as a broken catalog. */}
          {gapRule ? (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-warning/30 bg-warning-subtle px-3 py-2 text-sm">
              <TriangleAlert className="size-4 shrink-0 text-warning" aria-hidden />
              <span className="font-medium">{gapRule.label}</span>
              <span className="text-muted-foreground">
                {rows ? `${rows.length} mould${rows.length === 1 ? '' : 's'}` : '…'} · {gapRule.why}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto"
                onClick={() => setParam('gap', '')}
              >
                Show the whole catalog
              </Button>
            </div>
          ) : null}

          {showFilters ? (
            <div className="grid gap-2 rounded-xl border border-border bg-surface p-3 sm:grid-cols-3">
              <Select
                aria-label="Filter by project"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                placeholder="All projects"
                options={projects ?? []}
              />
              <Select
                aria-label="Filter by owner"
                value={ownerCompanyId}
                onChange={(e) => setOwnerCompanyId(e.target.value)}
                placeholder="All owners"
                options={companies ?? []}
              />
              <Select
                aria-label="Filter by tool type"
                value={toolTypeId}
                onChange={(e) => setToolTypeId(e.target.value)}
                placeholder="All tool types"
                options={toolTypes ?? []}
              />
              {activeFilters ? (
                <div className="sm:col-span-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setProjectId('');
                      setOwnerCompanyId('');
                      setToolTypeId('');
                    }}
                  >
                    <X className="size-3.5" /> Clear filters
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      }
    >
      {selected.size > 0 ? (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-primary bg-primary-subtle/40 px-3 py-2">
          <span className="text-sm font-medium">
            {selected.size} selected
          </span>
          {canCreate ? (
            <Button size="sm" variant="secondary" onClick={() => setBulkOpen(true)}>
              <Pencil className="size-3.5" /> Edit all
            </Button>
          ) : null}
          <Button size="sm" variant="secondary" onClick={() => setLabelSheet(true)}>
            <Printer className="size-3.5" /> Labels
          </Button>
          {canCreate ? (
            <Button size="sm" variant="secondary" onClick={() => setEngraveOpen(true)}>
              <PenTool className="size-3.5" /> Engrave files
            </Button>
          ) : null}
          {canDelete ? (
            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="size-3.5" /> Delete
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            Clear
          </Button>
        </div>
      ) : null}

      <DataTable
        rows={rows}
        columns={visibleColumns}
        selection={canCreate ? { selected, onChange: setSelected } : undefined}
        rowKey={(m) => m.id}
        onRowClick={(m) => navigate(`/moulds/${encodeURIComponent(m.assetCode)}`)}
        mobileLeading={(m) => <Thumb src={m.photoUrl} alt={m.name} count={m.photoCount} />}
        initialSort={{ key: 'assetCode', direction: 'asc' }}
        empty={
          search || activeFilters ? (
            <EmptyState
              icon={<Search />}
              title="No moulds match"
              description="Try a different search term, or clear the filters."
              action={
                <Button
                  variant="secondary"
                  onClick={() => {
                    setSearch('');
                    setProjectId('');
                    setOwnerCompanyId('');
                    setToolTypeId('');
                  }}
                >
                  Clear everything
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={<Package />}
              title="No moulds yet"
              description="Add the first mould, or wait for the catalog to finish downloading to this device."
              action={
                canCreate ? (
                  <Button variant="primary" asChild>
                    <Link to="/moulds/new">
                      <Plus className="size-4" /> Add mould
                    </Link>
                  </Button>
                ) : null
              }
            />
          )
        }
      />

      {exportProgress && exportProgress.total > 0 ? (
        <p role="status" className="mt-3 text-sm text-muted-foreground">
          Putting the photos in the file: {exportProgress.done} of {exportProgress.total}…
        </p>
      ) : null}
      {exportError ? (
        <p role="alert" className="mt-3 text-sm text-danger">
          {exportError}
        </p>
      ) : null}
      {exportResult ? (
        <div role="status" className="mt-3 text-sm text-muted-foreground">
          <p>
            Exported {exportResult.moulds} mould{exportResult.moulds === 1 ? '' : 's'} with {exportResult.photos} photo
            {exportResult.photos === 1 ? '' : 's'}.
            {exportResult.primaryOnly
              ? ' Offline, so each mould has only its main photo - export again with a connection for every photo.'
              : ''}
          </p>
          {exportResult.missed.length ? (
            <details className="mt-1 text-warning">
              <summary className="cursor-pointer">
                {exportResult.missed.length} mould{exportResult.missed.length === 1 ? ' has' : 's have'} a photo that
                could not be included - the file says so on each row
              </summary>
              <ul className="mt-1 list-disc pl-5">
                {exportResult.missed.slice(0, 50).map((x, k) => (
                  <li key={k}>
                    <span className="font-mono">{x.code}</span>: {x.reason}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      ) : null}

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        destructive
        loading={deleting}
        confirmLabel={`Delete ${selectedRows.length} mould${selectedRows.length === 1 ? '' : 's'}`}
        onConfirm={() => void deleteSelected()}
        title={`Delete ${selectedRows.length} mould${selectedRows.length === 1 ? '' : 's'}?`}
        description={
          <>
            <p>
              {selectedRows
                .slice(0, 8)
                .map((m) => m.assetCode)
                .join(', ')}
              {selectedRows.length > 8 ? ` and ${selectedRows.length - 8} more` : ''}.
            </p>
            {/* Worth saying plainly: the scan ledger is append-only, so the
                movement history of these tools survives them. People assume a
                delete takes everything, and hesitate over the wrong thing. */}
            <p className="mt-2 text-muted-foreground">
              Their scan history is kept - the ledger is never edited. This removes them from the
              catalog on every device at the next sync.
            </p>
          </>
        }
      />

      {labelSheet ? (
        <LabelSheetDialog moulds={selectedRows} open onOpenChange={setLabelSheet} />
      ) : null}

      {/* Mounted only while open, so it starts from the list as it is now. */}
      {engraveOpen ? (
        <EngraveBatchDialog
          open
          onOpenChange={setEngraveOpen}
          allCount={allRows?.length ?? 0}
          allIds={(allRows ?? []).map((m) => m.id)}
          visibleIds={isNarrowed ? (rows ?? []).map((m) => m.id) : null}
          selectedIds={selectedRows.map((m) => m.id)}
        />
      ) : null}

      <MouldImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
      />

      <MouldBulkEditDialog
        rows={selectedRows}
        open={bulkOpen && selectedRows.length > 0}
        onOpenChange={setBulkOpen}
        onDone={() => setSelected(new Set())}
      />

      {labelFor ? (
        <MouldLabelDialog
          mouldId={labelFor.id}
          assetCode={labelFor.assetCode}
          name={labelFor.name}
          owner={labelFor.ownerCompanyName}
          open
          onOpenChange={(open) => {
            if (!open) setLabelFor(null);
          }}
        />
      ) : null}
    </Page>
  );
}

/**
 * A mould's main photo, small, with how many more it has.
 *
 * A photo that will not load - a moved file, a dead link - falls back to the
 * same tile as no photo, rather than the browser's broken-image glyph.
 */
function Thumb({ src, alt, count = 0 }: { src: string | null; alt: string; count?: number }) {
  const [broken, setBroken] = useState(false);
  if (!src || broken) {
    return (
      <div
        className="grid size-10 place-items-center rounded-lg bg-surface-sunken text-subtle-foreground"
        title={src ? 'Photo could not be loaded' : 'No photo'}
      >
        <ImageOff className="size-4" aria-hidden />
      </div>
    );
  }
  return (
    <span className="relative inline-block size-10 shrink-0">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onError={() => setBroken(true)}
        className={cn('size-10 rounded-lg border border-border object-cover')}
      />
      {count > 1 ? (
        <span
          className="absolute -bottom-1 -right-1 min-w-4 rounded-full border border-surface bg-foreground px-1 text-center text-[10px] font-semibold leading-4 text-background tabular"
          title={`${count} photos`}
        >
          {count}
        </span>
      ) : null}
    </span>
  );
}

function Missing() {
  return <span className="text-subtle-foreground">-</span>;
}
